import { Link } from "react-router-dom";
import { Wordmark, GoldDivider, Eyebrow, EditorialHeading } from "../../components/brand";
import { BUSINESS_INFO } from "../../content/site";

/**
 * Site-wide Terms of Service & Privacy Notice.
 *
 * This is presented as a single luxury editorial page (not a wall of text)
 * so investors can read it end-to-end. It covers:
 *   - Acceptance of terms
 *   - Use of the site and services (advisory, opportunities, library)
 *   - Intellectual property
 *   - Privacy of personal data + cookies
 *   - Third-party services (Resend, OpenAI, Anthropic)
 *   - Disclaimers around investment content
 *   - Limitation of liability & governing law
 *   - Contact
 */
export default function Terms() {
  const updated = "February 2026";
  return (
    <div data-testid="page-terms">
      {/* Hero */}
      <section className="bg-altura-espresso text-altura-ivory pt-24 pb-20 relative noise overflow-hidden">
        <div className="container-editorial relative z-10 max-w-4xl">
          <Wordmark className="text-altura-ivory" />
          <Eyebrow className="text-altura-gold mt-8">Legal</Eyebrow>
          <EditorialHeading size="lg" className="mt-4 text-altura-ivory">
            Terms of Service & Privacy Notice
          </EditorialHeading>
          <GoldDivider className="mt-6" />
          <p className="mt-6 text-altura-ivory/70">Last updated · {updated}</p>
        </div>
      </section>

      <section className="bg-altura-ivory py-20">
        <div className="container-editorial max-w-3xl legal-prose">
          <Section title="1. Acceptance of these terms">
            By accessing or using <strong>alturaglobalre.com</strong> ("the Site") or any of the services we offer through it — including the Digital Library, opportunity listings, advisory consultations, and Ask AURI concierge — you agree to be bound by these Terms of Service and our Privacy Notice. If you do not accept these terms, please do not use the Site.
          </Section>

          <Section title="2. Who we are">
            The Site is operated by <strong>Altura Global Real Estate</strong>, an international real-estate advisory firm curating premium property opportunities and market intelligence across our flagship markets. References to "Altura Global", "we", "our", or "us" refer to Altura Global Real Estate.
          </Section>

          <Section title="3. What the Site does">
            The Site provides:
            <ul>
              <li>Curated opportunity listings and market intelligence across our served markets;</li>
              <li>A Digital Library of complimentary guides, checklists, and country briefings;</li>
              <li>Advisory consultation booking with our team of real-estate specialists;</li>
              <li>The AURI concierge — a text and voice AI assistant that answers questions about our markets, opportunities, and services;</li>
              <li>The Global Link (TGL) Affiliate Program — governed by a separate agreement. See <Link to="/tgl-terms" className="underline decoration-altura-gold text-altura-espresso hover:text-altura-gold-dark">TGL Terms &amp; Conditions</Link>.</li>
            </ul>
          </Section>

          <Section title="4. Not investment, tax, or legal advice">
            Opportunity listings, guides, checklists, and any editorial content on the Site are provided for general information only. Nothing on the Site constitutes financial, investment, tax, or legal advice. You should consult qualified professionals in your jurisdiction before making any investment decision. Past performance is not a guide to future results and property values can rise as well as fall.
          </Section>

          <Section title="5. Your account & submissions">
            When you submit a form, download a Library resource, or interact with AURI, you may share personal information such as your name, email address, phone number, country, and stated interests. You represent that the information you provide is accurate, and that you have the right to share it with us.
          </Section>

          <Section title="6. Privacy — what we collect, why, and how">
            <p>We collect:</p>
            <ul>
              <li><strong>Contact and profile information</strong> you submit through forms (name, email, phone, market interest);</li>
              <li><strong>Transcripts and summaries</strong> of your conversations with AURI (text and voice) so we can follow up with a human advisor and continually improve the concierge experience;</li>
              <li><strong>Standard technical data</strong> such as IP address, browser, and referring page for security, analytics, and abuse prevention.</li>
            </ul>
            <p>We use this data to respond to your inquiries, send you the resources you request, deliver services you sign up for, and — where you have opted in — send you updates about new opportunities. We do not sell your personal information. We use reputable service providers to process data on our behalf: <strong>Resend</strong> for transactional email, <strong>OpenAI</strong> for the Realtime Voice AURI, and <strong>Anthropic</strong> (Claude) for the text concierge and library recommendations. Data shared with these providers is subject to their respective privacy commitments.
            </p>
            <p>You may request access, correction, or deletion of your data at any time by writing to <a href={`mailto:${BUSINESS_INFO.email}`} className="underline text-altura-espresso decoration-altura-gold">{BUSINESS_INFO.email}</a>.</p>
          </Section>

          <Section title="7. Cookies & analytics">
            The Site uses a minimal set of cookies necessary for functionality (session, security, remembering your language). We do not use invasive advertising trackers. If we introduce optional analytics in the future you will be given the ability to opt-in.
          </Section>

          <Section title="8. Intellectual property">
            All content on the Site — including copy, photography, guides, videos, code, and brand assets — is the property of Altura Global Real Estate or its licensors, protected by international copyright and trademark laws. You may download and share complimentary Library resources for personal, non-commercial use only. You may not reproduce, redistribute, or create derivative works from any Altura Global content without our prior written permission.
          </Section>

          <Section title="9. Opportunities are subject to change">
            All property opportunities featured on the Site are subject to availability, prior sale, price change, and withdrawal without notice. Photography and floor plans are illustrative. Nothing on the Site constitutes an offer or contract for sale. Any transaction will be governed by separately-executed contracts and applicable local law.
          </Section>

          <Section title="10. Third-party links">
            The Site may contain occasional links to third-party websites for reference. Altura Global does not endorse and is not responsible for the content, policies, or practices of any third-party site.
          </Section>

          <Section title="11. Limitation of liability">
            To the fullest extent permitted by law, Altura Global Real Estate, its affiliates, officers, employees, and partners will not be liable for any indirect, incidental, consequential, or punitive damages arising from your use of the Site or any content, data, or services provided through it. The Site is provided on an "as-is" and "as-available" basis.
          </Section>

          <Section title="12. Changes to these terms">
            We may update these terms from time to time. Material changes will be reflected in the "Last updated" date above and, where appropriate, notified to registered users by email. Your continued use of the Site after a change constitutes acceptance of the updated terms.
          </Section>

          <Section title="13. Governing law">
            These terms are governed by the laws of the jurisdiction in which Altura Global Real Estate is principally domiciled, without regard to conflict-of-laws principles. Disputes will be resolved in the courts of competent jurisdiction in that domicile.
          </Section>

          <Section title="14. Contact">
            Questions about these terms, your data, or anything else? Reach us at{" "}
            <a href={`mailto:${BUSINESS_INFO.email}`} className="underline text-altura-espresso decoration-altura-gold">{BUSINESS_INFO.email}</a>{" "}
            or visit the <Link to="/contact" className="underline text-altura-espresso decoration-altura-gold">Contact</Link> page.
          </Section>

          <div className="mt-16 border-t border-altura-espresso/10 pt-8 text-sm text-altura-charcoal/60">
            © {new Date().getFullYear()} Altura Global Real Estate. All rights reserved.
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12 first:mt-0">
      <h2 className="font-editorial text-2xl text-altura-espresso leading-tight">{title}</h2>
      <div className="mt-4 space-y-4 text-[15px] text-altura-charcoal/80 leading-[1.75]">
        {children}
      </div>
    </section>
  );
}
